'use client'

import { useWallet } from '@/providers/WalletContext'
import { PublicKey } from '@solana/web3.js'
import { useQuery } from '@tanstack/react-query'
import { SOLANA_USDC_MINT } from './useDflowSwap'

// Get associated token account address
async function getAssociatedTokenAddress(mint: PublicKey, owner: PublicKey): Promise<PublicKey> {
	const TOKEN_PROGRAM_ID = new PublicKey('TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA')
	const ASSOCIATED_TOKEN_PROGRAM_ID = new PublicKey('ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL')

	const [address] = PublicKey.findProgramAddressSync(
		[owner.toBuffer(), TOKEN_PROGRAM_ID.toBuffer(), mint.toBuffer()],
		ASSOCIATED_TOKEN_PROGRAM_ID
	)
	return address
}

export function useSolanaUsdcBalance() {
	const { solanaAddress, solanaConnection, isSolanaReady } = useWallet()

	return useQuery({
		queryKey: ['solanaBalance', 'usdc', solanaAddress],
		queryFn: async () => {
			if (!(solanaConnection && solanaAddress)) {
				return null
			}

			try {
				const ownerPubkey = new PublicKey(solanaAddress)
				const mintPubkey = new PublicKey(SOLANA_USDC_MINT)
				const ata = await getAssociatedTokenAddress(mintPubkey, ownerPubkey)

				const accountInfo = await solanaConnection.getAccountInfo(ata)
				if (!accountInfo) {
					return { balance: 0, formatted: '0.00' }
				}

				// Parse token account data (SPL Token account layout)
				// Offset 64 is where the amount (u64) is stored
				const data = accountInfo.data
				const amount = data.readBigUInt64LE(64)
				const balance = Number(amount)

				// USDC has 6 decimals
				const formatted = (balance / 1_000_000).toFixed(2)

				return { balance, formatted }
			} catch (error) {
				console.error('Failed to fetch USDC balance:', error)
				return { balance: 0, formatted: '0.00' }
			}
		},
		enabled: isSolanaReady && !!solanaAddress && !!solanaConnection,
		staleTime: 5_000,
		refetchInterval: 10_000,
	})
}

export function useSolanaTokenBalance(mint: string | undefined) {
	const { solanaAddress, solanaConnection, isSolanaReady } = useWallet()

	return useQuery({
		queryKey: ['solanaBalance', 'token', mint, solanaAddress],
		queryFn: async () => {
			if (!(solanaConnection && solanaAddress && mint)) {
				return null
			}

			try {
				const ownerPubkey = new PublicKey(solanaAddress)
				const mintPubkey = new PublicKey(mint)
				const ata = await getAssociatedTokenAddress(mintPubkey, ownerPubkey)

				const accountInfo = await solanaConnection.getAccountInfo(ata)
				if (!accountInfo) {
					return { balance: 0, formatted: '0' }
				}

				const data = accountInfo.data
				const amount = data.readBigUInt64LE(64)
				const balance = Number(amount)

				// Outcome tokens typically have 6 decimals like USDC
				const formatted = (balance / 1_000_000).toFixed(2)

				return { balance, formatted }
			} catch (error) {
				console.error('Failed to fetch token balance:', error)
				return { balance: 0, formatted: '0' }
			}
		},
		enabled: isSolanaReady && !!solanaAddress && !!solanaConnection && !!mint,
		staleTime: 5_000,
		refetchInterval: 10_000,
	})
}
